'use client';

import { storyblokEditable } from '@storyblok/react/rsc';
import clsx from 'clsx';
import { FC } from 'react';
import { useForm } from 'react-hook-form';
import { Container } from 'atoms/Container';
import { HeadLine } from 'atoms/Typography/Typography';
import { NewsletterForm } from 'molecules/NewsletterForm';
import { useNewsletter } from 'modules/shop/newsletter/useNewsletter';
import { isValidDate } from 'modules/stroyblok/services';
import {
  useValidationRules,
  useYupValidationResolver,
} from 'modules/validation/hooks';
import { RichTextMarkdown } from '../RichText';
import { INewsletterBlock, INewsletterBlockBare } from './types';
import styles from './styles.module.css';

export const NewsletterBlockBare: FC<INewsletterBlockBare> = ({
  blok,
  handleSubscribe,
  className,
  control,
}) => {
  return (
    <div className={clsx(styles.root, className)} {...storyblokEditable(blok)}>
      <Container className={styles.container}>
        {blok?.title && (
          <HeadLine type="xs" weight="bold" className={styles.title}>
            {blok.title}
          </HeadLine>
        )}
        <div className={styles.content}>
          <NewsletterForm
            handleSubscribe={handleSubscribe}
            placeholder={blok?.input_placeholder || ''}
            button_text={blok?.button_text || ''}
            control={control}
            className={styles.form}
          />
          {blok?.text_under && (
            <RichTextMarkdown text={blok.text_under} className={styles.text} />
          )}
        </div>
      </Container>
    </div>
  );
};

export const NewsletterBlock: FC<INewsletterBlock> = ({
  blok,
  className,
  titleClassName,
}) => {
  const { email } = useValidationRules();
  const resolver = useYupValidationResolver({ email });
  const { subscribe } = useNewsletter();

  const { handleSubmit, control } = useForm({
    mode: 'onChange',
    resolver,
  });

  const isDateValid = isValidDate(blok?.startDate, blok?.expirationDate);

  const handleSubscribe = async ({ email }: { email: string }) => {
    return subscribe(email, true);
  };

  return !blok || !isDateValid ? null : (
    <NewsletterBlockBare
      blok={blok}
      handleSubscribe={handleSubmit(handleSubscribe)}
      control={control}
      className={className}
      titleClassName={titleClassName}
    />
  );
};
